import { useCreditUsage } from 'hooks/useCreditUsage'
import { CreditsGateProps } from './Types'
import { PressEvent, PressResponder } from '@react-aria/interactions'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

const CreditsGate = observer(({ charge, children, onPress, ...pressProps }: CreditsGateProps) => {
  const { creditsRemaining, canAutoReload } = useCreditUsage()
  const openPurchaseModal = usePurchaseContentCreditsModal()
  const {
    tierUsageStore: { autopilotAddOns },
  } = useRootStore()
  const autopilotContentCreditsAddon = autopilotAddOns.find(
    ({ key }) => key === PaymentPlanKey.BlazeAutopilotContentCredits,
  )
  const autopilotContentCreditsPrice = autopilotContentCreditsAddon?.prices[0]
  const needsCredits = charge > creditsRemaining
  const workspace = useCurrentWorkspace()
  const autoReloadBudgetReached = Boolean(workspace?.auto_reload_credits && !canAutoReload)

  const handlePress = useCallback(
    async (e: PressEvent) => {
      if (autoReloadBudgetReached && autopilotContentCreditsPrice) {
        return openPurchaseModal(
          {
            price: autopilotContentCreditsPrice,
            defaultBatchCount: Math.max(1, Math.ceil(Math.max(charge, 100) / 100)),
            onSuccess: () => onPress?.(e),
          },
          { newStack: true },
        )
      }

      if (needsCredits && autopilotContentCreditsPrice) {
        return openPurchaseModal(
          {
            price: autopilotContentCreditsPrice,
            defaultBatchCount: Math.ceil((charge - creditsRemaining) / 100),
            onSuccess: () => onPress?.(e),
          },
          { newStack: true },
        )
      }

      return onPress?.(e)
    },
    [
      autoReloadBudgetReached,
      needsCredits,
      autopilotContentCreditsPrice,
      charge,
      creditsRemaining,
      openPurchaseModal,
      onPress,
    ],
  )

  return (
    <PressResponder onPress={handlePress} {...pressProps}>
      {children}
    </PressResponder>
  )
})

export { CreditsGate }
